import React, { type Ref, forwardRef, useEffect, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { FormTitle } from '../../form/form-title.component';
import { getOccupationFormSettings } from './occupation-form-settings';
import { cloneDeep, debounce } from 'lodash';
import { v4 } from 'uuid';
import { DynamicForm } from '@myosh/odin-components';
import type {
  DataGridRef,
  DynamicFormButtonSetting,
  DynamicFormRef,
  JsonDataItem,
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
  OdinDataSender,
  DialogRef,
} from '@myosh/odin-components';
import type { ActiveRecordContextProps } from '../../active-record/active-record.component';
import { FormLoading } from '../../form/form-loading.component';
import { BehaviorSubject, Subject, skip, takeUntil } from 'rxjs';
import { getDataGridReference } from '../../../services/data-grid.service';
import { useAppDispatch } from '../../../redux/hooks';
import {
  useGetOccupationByIdQuery,
  useUpdateOccupationMutation,
  useAddOccupationMutation,
  occupationApi,
} from '../../../redux/services/occupation';
import { archieveButton, getArchiveRestoreMessages, restoreButton, saveButton } from '../../../pages/admin/admin-utils';
import usePerformingSaveToast from '../../../hooks/use-performing-save-toast';
import { promiseToast, showError, showSuccess } from '../../../services/notification.service';
import { useTranslation } from 'react-i18next';
import useDynamicFormNotifier from '../../../hooks/use-dynamic-form-notifier';
import CloseActiveItemButton from '../../common/close-active-item-button';
import { useUserAccess } from '../../../hooks/use-user-access';
import { OccupationsPermissions } from '../../../utils/user.permissions';
import type {
  OccupationItem,
  OccupationMutationResponse,
  OccupationMutationErrorResponse,
} from '../../../@types/occupations';
import { updateOccupationData, getOccupationFieldOptionsData } from '../../../utils/occupation-functions';
import type { ExtendedOccupationItem } from '../../../utils/occupation-functions';
import type { FormChangedFields } from '../../../@types/common';
import { getChangedFieldsData } from '../../../utils/common-administration-utils';
import { forceAssert } from '../../../utils/type-guards';
import type { ExtendedDynamicFormSettings } from '../../../@types/forms';
import ConfirmationDialog from '../../common/confirmation-dialog.component';

export interface OccupationDetailsProps {
  occupationId: number;
  title: string;
  isNewOccupation?: boolean;
  onClose: (id: string) => void;
  panelSettings?: BehaviorSubject<ActiveRecordContextProps>;
  onCloseActiveRecordConfirmationModalRef?: DialogRef | null;
}
interface OccupationDetailsRef {
  submitForm?: () => void;
}

const TypeOfError = {
  DUPLICATED: 'DUPLICATED',
  FAILED: 'FAILED',
} as const;
type TypeOfError = (typeof TypeOfError)[keyof typeof TypeOfError];

const OccupationDetails = (props: OccupationDetailsProps, ref: Ref<OccupationDetailsRef>) => {
  const {
    occupationId,
    title,
    isNewOccupation = false,
    onClose,
    panelSettings,
    onCloseActiveRecordConfirmationModalRef,
  } = props;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [occupation, setOccupation] = useState<ExtendedOccupationItem>();
  const [panelContextProps, setPanelContextProps] = useState<ActiveRecordContextProps>();
  const [formButtons, setFormButtons] = useState<Array<DynamicFormButtonSetting>>();
  const [formSettings, setFormSettings] = useState<ExtendedDynamicFormSettings>();

  const dynamicFormReference = useRef<DynamicFormRef>(null);
  const saveModalReference = useRef<DialogRef>(null);
  const dataGridRef = useRef<DataGridRef | undefined>(undefined);
  const destroySubject = useRef(new Subject<void>());
  const dynamicFormId = useRef(v4());
  const closeOnModalSaveRef = useRef(false);

  const { data, isLoading, isFetching } = useGetOccupationByIdQuery(occupationId, {
    skip: occupationId === undefined,
  });

  const { readOnly, editAccess, setReadOnly, isExternalUser } = useUserAccess({
    initialAccess: isNewOccupation,
    permissions: {
      MODIFY: [OccupationsPermissions.OCCUPATION_MODIFY, OccupationsPermissions.OCCUPATION_CREATE],
      READ: OccupationsPermissions.OCCUPATION_READ,
    },
  });

  const [addOccupation] = useAddOccupationMutation();
  const [updateOccupation] = useUpdateOccupationMutation();

  const { notifyDirty, notifySaveSucceeded, notifySaveFailed } = useDynamicFormNotifier(dynamicFormId.current);

  const { showPerformingSaveToast, hidePerformingSaveToast } = usePerformingSaveToast({
    shouldFreezeActiveRecord: true,
    disabled: panelContextProps?.batchSaveInProgress,
  });

  useEffect(() => {
    if (!isFetching) {
      setOccupation(cloneDeep(data));
      const newFormButtons: Array<DynamicFormButtonSetting> = [];
      if (data?.archived) {
        newFormButtons.push({ ...restoreButton, onClick: () => onArchieveOccupation(false) });
      } else {
        newFormButtons.push(saveButton);
        if (!isNewOccupation) {
          newFormButtons.push({ ...archieveButton, onClick: () => onArchieveOccupation(true) });
        }
      }
      if (!editAccess) {
        setFormButtons(newFormButtons.map((button) => ({ ...button, disabled: true })));
      } else {
        setFormButtons(newFormButtons);
      }
    }
  }, [data, isFetching, editAccess]);

  useEffect(() => {
    panelSettings?.pipe(skip(1), takeUntil(destroySubject.current)).subscribe((settings) => {
      setPanelContextProps(settings);
    });

    onCloseActiveRecordConfirmationModalRef
      ?.subscribeToDialogResult?.()
      ?.pipe(takeUntil(destroySubject.current))
      .subscribe((result) => {
        if (result.buttonName === 'save' && dynamicFormReference.current?.formIsDirty()) {
          dynamicFormReference.current?.submitForm(true);
        } else if (result.buttonName === 'discard') {
          onClose(dynamicFormId.current);
        }
      });

    getDataGridReference()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((gridRef) => {
        if (gridRef) {
          dataGridRef.current = gridRef;
        }
      });

    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    submitForm: dynamicFormReference.current?.submitForm,
  }));

  const onArchieveOccupation = (archive: boolean) => {
    if (occupationId) {
      promiseToast(
        updateOccupation({ id: occupationId, archived: archive })
          .then(() => {
            onClose(dynamicFormId.current);
            dataGridRef.current?.api.data.getDataOverwritePageCache();
            dispatch(occupationApi.util.invalidateTags([{ type: 'Occupation', id: 'LIST' }]));
          })
          .catch(() => showErrorNotificationToast(TypeOfError.FAILED)),
        getArchiveRestoreMessages(archive)
      );
    }
  };

  useEffect(() => {
    if (occupation?.archived) {
      setReadOnly(true);
    }
  }, [occupation, setReadOnly]);

  useEffect(() => {
    setFormSettings(getOccupationFormSettings(readOnly));
  }, [readOnly]);

  const showSaveToast = () => {
    if (!panelContextProps?.batchSaveInProgress) {
      showSuccess(t('record-saved'));
    }
    notifySaveSucceeded(dynamicFormId.current);
    closeOnModalSaveRef.current = true;
    if (!occupationId || closeOnModalSaveRef.current) {
      onClose(dynamicFormId.current);
    }
  };

  function occupationErrorType(response: OccupationMutationErrorResponse) {
    const errorResult = response.error.data.validation.errors?.[0];
    if (!errorResult) return 'FAILED';
    const startIndex = errorResult.indexOf(':') + 2;
    if (errorResult.substring(startIndex) === 'Duplicate caption on Occupation!') {
      return 'DUPLICATED';
    } else {
      return 'FAILED';
    }
  }

  const showErrorNotificationToast = (convertedResponse: OccupationMutationErrorResponse | TypeOfError) => {
    if (!panelContextProps?.batchSaveInProgress) {
      const errorType =
        typeof convertedResponse === 'string' ? convertedResponse : occupationErrorType(convertedResponse);
      if (errorType === TypeOfError.DUPLICATED) {
        showError(t('duplicate-occupation-error'));
      } else {
        showError(t('occupation-details-save-failed'));
      }
    }

    notifySaveFailed(dynamicFormId.current);
  };

  const createOccupation = useCallback((data: JsonDataItem) => {
    updateOccupationData(data);
    saveOccupationData(data, (response) => {
      const convertedResponse = forceAssert<OccupationMutationResponse>(response);
      if ('data' in convertedResponse && convertedResponse.data?.result) {
        hidePerformingSaveToast();
        showSaveToast();
        refreshGrid();
      } else if ('error' in convertedResponse) {
        hidePerformingSaveToast();
        showErrorNotificationToast(convertedResponse);
      }
    });
  }, []);

  const onFormSubmit = (data: JsonDataItem) => {
    if (occupationId && occupation) {
      const dirtyFields = dynamicFormReference.current?.formDirtyFields() || {};
      const patchData = getChangedFieldsData(forceAssert<FormChangedFields<OccupationItem>>(dirtyFields), data);
      updateOccupationData(patchData);
      saveOccupationData({ id: occupationId, ...patchData }, (response) => {
        const convertedResponse = forceAssert<OccupationMutationResponse>(response);
        if ('data' in convertedResponse && convertedResponse.data?.result) {
          dataGridRef.current?.api.data.recordChanged(occupationId);
          hidePerformingSaveToast();
          showSaveToast();
        } else if ('error' in convertedResponse) {
          hidePerformingSaveToast();
          showErrorNotificationToast(convertedResponse);
        }
      });
    } else {
      createOccupation(data);
    }
  };

  const refreshGrid = () => {
    dataGridRef.current?.api.data.getDataOverwritePageCache();
  };

  const saveOccupationData = (
    occupationData: Partial<OccupationItem>,
    successCallback: (response?: unknown) => void
  ) => {
    showPerformingSaveToast();
    if (occupationData.id) {
      updateOccupation(occupationData)
        .then(successCallback)
        .catch(() => {
          showErrorNotificationToast(TypeOfError.FAILED);
        });
    } else {
      addOccupation([occupationData] as Array<OccupationItem>)
        .then(successCallback)
        .catch(() => {
          showErrorNotificationToast(TypeOfError.FAILED);
        });
    }
  };

  const onSaveConfirm = () => {
    const data = dynamicFormReference.current?.getData() || {};
    onFormSubmit(data);
  };

  const onSaveCancel = () => onClose(dynamicFormId.current);

  const onCloseOccupationDetails = () => {
    if (!dynamicFormReference.current?.formIsDirty()) {
      onClose(dynamicFormId.current);
    } else {
      saveModalReference.current?.show();
    }
  };

  const dataRetrieval: OdinDataRetrieval = {
    getData: (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if ('COMBOBOX' === options?.fieldType) {
        getOccupationFieldOptionsData(subscriber, options, isExternalUser);
      } else {
        subscriber.sendData();
      }
    },
  };

  const onFieldChanged = debounce(() => notifyDirty(dynamicFormReference.current), 500);

  return (
    <>
      <div className="flex flex-shrink-0 flex-col">
        <div className="flex">
          <FormTitle title={title} />
          {!isLoading && (
            <div className="flex min-w-min justify-end">
              <CloseActiveItemButton onClick={onCloseOccupationDetails} />
            </div>
          )}
        </div>
      </div>
      <div className="admin-form-default">
        {!formSettings && <FormLoading />}
        {formSettings && (
          <DynamicForm
            ref={dynamicFormReference}
            dynamicFormId={dynamicFormId.current}
            data={occupation ?? {}}
            settings={formSettings}
            dataRetrieval={dataRetrieval}
            buttons={formButtons}
            buttonsLocation={0}
            readOnly={readOnly}
            buttonsPosition={1}
            onSubmit={onFormSubmit}
            onFieldChanged={onFieldChanged}
          />
        )}
      </div>
      <ConfirmationDialog
        ref={saveModalReference}
        onConfirm={onSaveConfirm}
        onClose={onSaveCancel}
        cancelButtonText={t('discard')}
        hasCancelButton
      />
    </>
  );
};
export default forwardRef(OccupationDetails);
